import Filter from './Filter.js';
import { isNumeric, addUnits } from './helpers.js';

/**
 * Handles Dropdown creation
 */
class Checkboxes extends Filter {
  constructor(containerId, searchParameter, options, updateCallback, searchParameters) {
    super({ containerId, searchParameter, options, updateCallback, searchParameters });
    this.container = document.getElementById(containerId);
    this.key = this.container.dataset.key;
  }

  create() {
    let minValue, maxValue, searchIds;

    // Clear the existing widget.
    this.container.innerHTML = '';

    this.container.classList.add('form-group');
    let checkboxTree = document.createElement('ul');
    checkboxTree.classList.add('checkbox-tree');
    this.container.appendChild(checkboxTree);

    if (isNumeric(this.searchParameter)) {
      if (this.searchParameters[`${this.searchParameter}_min`]) {
        minValue = parseInt(this.searchParameters[`${this.searchParameter}_min`]);
      } else {
        minValue = this.options.minValue;
      }

      if (this.searchParameters[`${this.searchParameter}_max`]) {
        maxValue = parseInt(this.searchParameters[`${this.searchParameter}_max`]);
      } else {
        maxValue = this.options.maxValue;
      }

      for (let i = this.options.minValue; i < this.options.maxValue; i += this.options.step) {
        let optionContainer = document.createElement('li');
        optionContainer.classList.add('checkbox-tree__node');

        let input = document.createElement('input');
        input.type = 'checkbox';
        input.id = `${this.containerId}_${i}`;
        input.name = `dsp_${this.searchParameter}[]`;
        input.classList.add('form-checkbox-input');
        input.dataset.min = i;
        input.dataset.max = i + this.options.step;

        if (i + this.options.step <= maxValue && i >= minValue) {
          input.setAttribute('checked', true);
        }

        let label = document.createElement('label');
        label.innerText =
          addUnits(this.searchParameter, i) +
          ' - ' +
          addUnits(this.searchParameter, i + this.options.step);
        label.setAttribute('for', input.id);
        label.classList.add('form-check-label');

        checkboxTree.appendChild(optionContainer);
        optionContainer.appendChild(input);
        optionContainer.appendChild(label);
      }
      return;
    }

    if (this.searchParameters[this.searchParameter]) {
      searchIds = this.searchParameters[this.searchParameter].split(',');
    }

    if (!this.options) {
      this.container.dataset.optionCount = 0;
      return;
    }

    let optionCount = 0;
    this.options.forEach((option) => {
      const optionKey = this.getOptionKey(option);
      let optionItem = document.createElement('li');
      optionItem.classList.add('checkbox-tree__node');
      let optionContainer = document.createElement('div');
      optionContainer.classList.add('form-check');
      let categoryList;

      if (option.level === 0) {
        optionContainer.classList.add('accordion');
        optionContainer.setAttribute('id', `category${option.id}Accordion`);

        let categoryHeading = document.createElement('li');
        categoryHeading.innerText = `${option.name} (${option.doc_count})`;
        categoryHeading.classList.add('checkbox-tree_node');

        checkboxTree.appendChild(categoryHeading);

        categoryList = document.createElement('ul');
        categoryList.classList.add('checkbox-tree__child');
        categoryList.classList.add(`collapse${option.id}`);
        checkboxTree.appendChild(categoryList);
      } else {
        let inputId = `${this.containerId}-${option.id ?? `-${optionCount}`}`;
        let label = document.createElement('label');
        label.classList.add('form-check-label');
        label.setAttribute('for', inputId);

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.value = optionKey;
        input.id = inputId;
        input.name = `dsp_${this.searchParameter}[]`;
        input.classList.add('form-check-input');

        const labelText = document.createElement('span');
        if (this.searchParameter === 'ext_colour' || this.searchParameter === 'int_colour') {
          labelText.innerText = `${option.id} (${option.doc_count})`;
        } else {
          labelText.innerText = `${option.name} (${option.doc_count})`;
        }

        if (searchIds) {
          if (searchIds.includes(optionKey)) {
            input.checked = true;
          }

          // For backwards compatibility, option ids should always work.
          if (typeof option.id !== 'undefined') {
            if (searchIds.includes(option.id.toString())) {
              input.checked = true;
            }
          }
        }

        label.appendChild(input);
        label.appendChild(labelText);

        checkboxTree.appendChild(optionItem);
        if (option.level === 1) {
          let mainLevel = option.path[0].slice(0, 2);
          categoryList = document.querySelector(`.collapse${mainLevel}`);
          categoryList.appendChild(optionItem);
        }
        optionItem.appendChild(optionContainer);
        optionContainer.appendChild(label);

        optionCount += 1;
      }
    });

    this.container.dataset.optionCount = optionCount;
  }

  update() {
    let searchParameter = this.searchParameter;
    let searchParameters = this.searchParameters;
    let checkboxes = document.querySelectorAll(`input[name='dsp_${searchParameter}[]']`);
    let updateCallback = this.updateCallback;

    if (isNumeric(searchParameter)) {
      checkboxes.forEach((activeCheckbox) => {
        activeCheckbox.addEventListener('change', () => {
          let checkedCheckboxes = document.querySelectorAll(
            `input[name='dsp_${searchParameter}[]']:checked`,
          );
          if (!checkedCheckboxes.length) {
            searchParameters[`${searchParameter}_min`] = null;
            searchParameters[`${searchParameter}_max`] = null;
            typeof updateCallback === 'function' && updateCallback(searchParameter);
            return;
          }
          let min = checkedCheckboxes[0].dataset.min;
          let max = checkedCheckboxes[0].dataset.max;
          for (let i = 1; i < checkedCheckboxes.length; i++) {
            if (checkedCheckboxes[i].dataset.min === checkedCheckboxes[i - 1].dataset.max) {
              max = checkedCheckboxes[i].dataset.max;
            } else {
              checkedCheckboxes.forEach((checkbox) => {
                checkbox.checked = false;
              });
              activeCheckbox.checked = true;
              min = activeCheckbox.dataset.min;
              max = activeCheckbox.dataset.max;
              break;
            }
          }
          searchParameters[`${searchParameter}_min`] = min;
          searchParameters[`${searchParameter}_max`] = max;

          if (this.updateCallback && typeof this.updateCallback === 'function') {
            this.updateCallback(searchParameter);
          }
        });
      });
      return;
    }

    checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', () => {
        searchParameters[searchParameter] = '';
        let checkedCheckboxes = document.querySelectorAll(
          `input[name='dsp_${searchParameter}[]']:checked`,
        );

        searchParameters[searchParameter] = Array.from(checkedCheckboxes)
          .map((checkedCheckbox) => checkedCheckbox.value)
          .join(',');

        if (this.updateCallback && typeof this.updateCallback === 'function') {
          this.updateCallback(searchParameter);
        }
      });
    });
  }

  getOptionKey(option) {
    let key = '';
    if (typeof this.key !== 'undefined') {
      key = option[this.key];
    } else if (this.searchParameter === 'transmission_type') {
      key = option.name ?? option.id;
    } else {
      key = option.slug ?? option.id ?? option.name;
    }

    // The 'other-unsure' slug often does not work.
    if (key === 'other-unsure' && option.id) {
      key = option.id;
    }

    return key;
  }
}

export default Checkboxes;
